'use client';

import { Home, Phone, HelpCircle, Wallet } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React from 'react';
import { observer } from 'mobx-react-lite';

import { useProductStore, useModalStore } from '@/presentation/hooks/useStores';
import { Separator } from '@/components/ui/separator';

interface MobileNavigationProps {
  onClose: () => void;
  onRequestCallback: () => void;
  onOpenShoppingWallet?: () => void;
  showShoppingWallet?: boolean;
}

/**
 * Mobile navigation items for the side drawer.
 * Focuses on browsing (Home, Categories) and Support.
 * Account actions (Orders, Rep Score, Profile) are in the header dropdown.
 */
const MobileNavigation = observer(({
  onClose,
  onRequestCallback,
  onOpenShoppingWallet,
  showShoppingWallet = false,
}: MobileNavigationProps) => {
  const router = useRouter();
  const productStore = useProductStore();
  const modalStore = useModalStore();
  const categories = productStore.categoryNavigation;

  const handleNavClick = (path: string) => {
    router.push(path);
    onClose();
  };

  const openCallbackModal = () => {
    onClose(); // Close the drawer first
    onRequestCallback(); // Then open the modal (rendered in Header, outside the drawer)
  };

  const handleOpenHelpCenter = () => {
    onClose(); // Close the drawer first
    modalStore.openHelpCenterSheet(); // Then open the Help Center sheet
  };

  const handleOpenShoppingWallet = () => {
    if (!showShoppingWallet || !onOpenShoppingWallet) {
      return;
    }
    onClose();
    onOpenShoppingWallet();
  };

  return (
    <>
      <nav className="flex flex-col gap-4 py-4">
        {/* Main Navigation */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => handleNavClick('/')}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
          >
            <Home className="size-5 text-gray-600" />
            <span className="font-medium">Home</span>
          </button>
        </div>

        {showShoppingWallet && onOpenShoppingWallet ? (
          <>
            <Separator />

            <div className="flex flex-col gap-1">
              <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Account
              </p>
              <button
                onClick={handleOpenShoppingWallet}
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
                data-testid="shopping-wallet-mobile-menu-item"
              >
                <Wallet className="size-5 text-gray-600" />
                <span>Shopping Wallet</span>
              </button>
            </div>
          </>
        ) : null}

        <Separator />

        {/* Browse Categories */}
        <div className="flex flex-col gap-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Browse Categories
          </p>
          {productStore.isLoading ? (
            <p className="px-3 py-2 text-sm text-gray-500">Loading categories...</p>
          ) : (
            categories.map((category) => (
              <Link
                key={category.id}
                href={`/category/${category.slug}`}
                onClick={onClose}
                className="rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
              >
                <span>{category.name}</span>
              </Link>
            ))
          )}
        </div>

        <Separator />

        {/* Support */}
        <div className="flex flex-col gap-1">
          <p className="px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Support
          </p>

          <button
            onClick={openCallbackModal}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
          >
            <Phone className="size-5 text-gray-600" />
            <span>Request a call back</span>
          </button>

          <button
            onClick={handleOpenHelpCenter}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-gray-100"
          >
            <HelpCircle className="size-5 text-gray-600" />
            <span>Help center</span>
          </button>
        </div>
      </nav>
    </>
  );
});

MobileNavigation.displayName = 'MobileNavigation';

export default MobileNavigation;
